import { WizardFormData } from '../IntegrationWizard'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'

interface Step1BasicInfoProps {
  formData: WizardFormData
  setFormData: (data: WizardFormData) => void
  errors: Record<string, string>
  isEditing: boolean
}

export default function Step1BasicInfo({ formData, setFormData, errors, isEditing }: Step1BasicInfoProps) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold mb-2">Integration Type & Basic Information</h2>
        <p className="text-sm text-muted-foreground">
          Select the type of integration and provide basic details
        </p>
      </div>

      {/* Integration Type Selector - Only show when creating new */}
      {!isEditing && (
        <div className="space-y-4">
          <Label className="text-base font-medium">Integration Type *</Label>
          <div className="space-y-3">
            <label
              className={`flex items-start space-x-3 p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                formData.integration_type === 'openai_compatible'
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/50'
              }`}
              onClick={() => setFormData({ ...formData, integration_type: 'openai_compatible' })}
            >
              <input
                type="radio"
                checked={formData.integration_type === 'openai_compatible'}
                onChange={() => setFormData({ ...formData, integration_type: 'openai_compatible' })}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="font-medium">OpenAI Compatible Endpoint</div>
                <div className="text-sm text-muted-foreground mt-1">
                  Standard OpenAI chat completions API format. Ideal for LLM-based services.
                </div>
              </div>
            </label>

            <label
              className={`flex items-start space-x-3 p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                formData.integration_type === 'generic_api'
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/50'
              }`}
              onClick={() => setFormData({ ...formData, integration_type: 'generic_api' })}
            >
              <input
                type="radio"
                checked={formData.integration_type === 'generic_api'}
                onChange={() => setFormData({ ...formData, integration_type: 'generic_api' })}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="font-medium">Generic Conversation Endpoint</div>
                <div className="text-sm text-muted-foreground mt-1">
                  Custom API with flexible response parsing. Configure JSON path for response extraction.
                </div>
              </div>
            </label>

            <label
              className={`flex items-start space-x-3 p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                formData.integration_type === 'crawler'
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/50'
              }`}
              onClick={() => setFormData({ ...formData, integration_type: 'crawler' })}
            >
              <input
                type="radio"
                checked={formData.integration_type === 'crawler'}
                onChange={() => setFormData({ ...formData, integration_type: 'crawler' })}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="font-medium">Web Crawler</div>
                <div className="text-sm text-muted-foreground mt-1">
                  Real-time web search for current information. No API credentials needed.
                </div>
              </div>
            </label>

            <label
              className={`flex items-start space-x-3 p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                formData.integration_type === 'sro'
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/50'
              }`}
              onClick={() => setFormData({ ...formData, integration_type: 'sro', name: 'Service Requests' })}
            >
              <input
                type="radio"
                checked={formData.integration_type === 'sro'}
                onChange={() => setFormData({ ...formData, integration_type: 'sro', name: 'Service Requests' })}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="font-medium">Service Request Orchestrator</div>
                <div className="text-sm text-muted-foreground mt-1">
                  Built-in service request handling (report issues, check status). No API credentials needed.
                </div>
              </div>
            </label>
          </div>
        </div>
      )}

      {/* Name and Description - Hidden for crawler and sro */}
      {!['crawler', 'sro'].includes(formData.integration_type) && (
        <>
          <div className="space-y-2">
            <Label htmlFor="name">Integration Name *</Label>
            <Input
              id="name"
              placeholder="e.g., Municipal Service Requests"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className={errors.name ? 'border-destructive' : ''}
            />
            <p className="text-xs text-muted-foreground">
              A descriptive name to identify this integration in routing and logs.
            </p>
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              rows={3}
              placeholder="Handles citizen service requests like potholes, fallen trees, etc."
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
            <p className="text-xs text-muted-foreground">
              Brief description to help the AI understand when to route queries to this integration.
            </p>
          </div>
        </>
      )}

      {/* Active Status */}
      <div className="flex items-start space-x-3 p-4 rounded-lg border bg-muted/30">
        <Checkbox
          id="is_active"
          checked={formData.is_active}
          onCheckedChange={(checked) =>
            setFormData({ ...formData, is_active: checked as boolean })
          }
          className="mt-0.5"
        />
        <div className="space-y-1 flex-1">
          <Label htmlFor="is_active" className="cursor-pointer font-medium">
            Active Integration
          </Label>
          <p className="text-sm text-muted-foreground">
            When enabled, this integration will be available for routing. You can disable it temporarily without deleting.
          </p>
        </div>
      </div>

      {formData.integration_type === 'crawler' && (
        <div className="p-4 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-900 rounded-lg">
          <p className="text-sm text-blue-900 dark:text-blue-100">
            <strong>Note:</strong> Web crawler integration uses your tenant's search API key configured in system settings.
            No additional credentials needed at integration level.
          </p>
        </div>
      )}

      {formData.integration_type === 'sro' && (
        <div className="p-4 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-900 rounded-lg">
          <p className="text-sm text-green-900 dark:text-green-100">
            <strong>Note:</strong> SRO uses your configured service request settings. No API credentials or endpoint needed.
            You can select a specific SRO configuration in the next step.
          </p>
        </div>
      )}
    </div>
  )
}
